import BaseModal from "../../../components/ui/BaseModal";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import { CurrencySymbol, UserType } from "../../../utils/enum";
import { useFormik } from "formik";
import * as yup from "yup";
import { createInvoice } from "../../../services/api/invoice";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useNavigate } from "react-router-dom";
import { fetchAllBankAccountsByBranch } from "../../../services/api/bankAccount";
import { useEffect, useRef, useState } from "react";
import { fetchCompanyBranches } from "../../../services/api/branch";
import { getClientAccountById } from "../../../services/api/client";

const validationSchema = yup.object({
  client: yup.object({
    clientId: yup
      .number()
      .typeError("Client is required")
      .notRequired("Client is required"),
    clientName: yup.string().nullable(),
  }),
  engineer: yup.object({
    engineerId: yup
      .number()
      .typeError("Engineer is required")
      .notRequired("Engineer is required"),
    engineerName: yup.string().nullable(),
  }),
  paymentTerms: yup
    .number()
    .typeError("Select Payment Terms")
    .required("Payment Terms is required"),
  currency: yup.string().required("Currency is required"),
  bankAccountId: yup.string().required("Bank account is required"),
  branchId: yup.number().required("Select branch"),
});

const GenerateInvoiceModal = ({
  open,
  close,
  engineer,
  client,
  selectedWorkOrderIds,
  currency,
  role,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: clientAccountData, isLoading: isFetching } = useQuery({
    queryKey: ["clientAccount", client?.clientId],
    queryFn: () => getClientAccountById(client?.clientId, true),
    enabled: !!client?.clientId,
  });
  const formik = useFormik({
    initialValues: {
      client: { clientName: client?.clientName, clientId: client?.clientId },
      engineer: {
        engineerName: engineer?.providerName,
        engineerId: engineer?.providerId,
      },
      paymentTerms: clientAccountData?.data?.paymentTerms || null,
      currency: currency,
      taxType: clientAccountData?.data?.taxType || null,
      taxPercentage: clientAccountData?.data?.taxPercentage || null,
      bankAccountId: null,
      branchId: client?.branchId || null,
      selectedBankAccount: null,
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      handleGenerateInvoiceButton();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });
  const allCurrencies = Object.keys(CurrencySymbol);

  const {
    isPending: isCreateInvoiceApiLoading,
    mutateAsync: createInvoiceMutation,
  } = useMutation({
    mutationFn: createInvoice,
  });

  const {
    data: branchesData,
    isPending: isFetchCompanyBranchesApiLoading,
    error: isFetchCompanyBranchesApiError,
  } = useQuery({
    queryKey: ["branchesData"],
    queryFn: () => fetchCompanyBranches(),
  });

  const {
    data: bankAccountsData,
    isPending: isFetchBankAccountsApiLoading,
    error: isFetchBankAccountsApiError,
  } = useQuery({
    queryKey: ["branchData", formik.values.branchId],
    queryFn: () => fetchAllBankAccountsByBranch(formik.values.branchId),
    enabled: !!formik.values.branchId,
  });

  const formattedBankAccount =
    bankAccountsData?.data
      ?.filter((account) => account.currency === currency)
      ?.map((account) => ({
        label: account.accountHolderName,
        value: account.id,
      })) || [];

  const formattedBranchesData =
    branchesData?.data?.data?.map((branch) => {
      return {
        label: branch.name,
        value: branch.id,
      };
    }) || [];

  const { bankAccountId, selectedBankAccount, branchId } = formik.values;
  useEffect(() => {
    const bankAccount = bankAccountsData?.data?.find(
      (account) => account.id === bankAccountId
    );

    if (bankAccount?.id !== selectedBankAccount?.id) {
      formik.setFieldValue("selectedBankAccount", bankAccount || null);

      if (bankAccount && bankAccount.currency !== formik.values.currency) {
        formik.setFieldValue("currency", bankAccount.currency);
      }
    }
  }, [bankAccountId, bankAccountsData?.data]);

  useEffect(() => {
    formik.setFieldValue("bankAccountId", null);
    formik.setFieldValue("selectedBankAccount", null);
  }, [branchId]);

  useEffect(() => {
    if (
      formattedBankAccount.length === 1 &&
      bankAccountId !== formattedBankAccount[0].value
    ) {
      formik.setFieldValue("bankAccountId", formattedBankAccount[0].value);
    }
  }, [formattedBankAccount]);
  const handleGenerateInvoiceButton = async () => {
    try {
      const payload = {
        clientId: formik.values.client.clientId,
        engineerId: formik.values.engineer.engineerId,
        currency: formik.values.currency,
        paymentTerms: formik.values.paymentTerms,
        taxType: formik.values.taxType,
        taxPercentage: formik.values.taxPercentage || 0,
        workOrderIds: selectedWorkOrderIds,
        bankAccountId: formik.values?.bankAccountId,
        branchId: formik.values?.branchId,
        role: role,
      };

      const invoice = await createInvoiceMutation(payload);

      dispatch(
        showToast({
          type: "success",
          message: "Invoice successfully generated",
        })
      );
      if (role === UserType.CLIENT) {
        navigate("/invoices/customer");
      } else {
        navigate("/invoices/engineer");
      }
    } catch (error) {
      const errors = error?.response?.data?.message;

      if (Array.isArray(errors)) {
        errors.forEach((err) =>
          dispatch(
            showToast({
              message: err,
              type: "error",
            })
          )
        );
      } else {
        dispatch(
          showToast({
            message: errors || "Something went wrong",
            type: "error",
          })
        );
      }
    }
  };

  return (
    <BaseModal
      className="-top-[200px] overflow-auto"
      open={open}
      close={() => {
        close(false);

        // formik.resetForm();
      }}
    >
      <div className="py-7 px-12 xl:w-[1000px] lg:w-[900px] sm:w-[700px]">
        <h6 className="text-dark font-normal border-b pb-1">
          Generate Invoice
        </h6>

        <div className="grid grid-cols-2 gap-x-10 gap-y-4 mt-4">
          {role === UserType.FREELANCER && (
            <BaseTextField
              disabled={true}
              label="Provider Name"
              isDefaultField={false}
              labelShrink={false}
              optional={false}
              id="engineer"
              name="engineer"
              value={formik.values.engineer.engineerName}
              // onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              /* error={
              formik.touched.companyName && Boolean(formik.errors.companyName)
            } */
              // helperText={formik.touched.companyName && formik.errors.companyName}
            />
          )}
          {role === UserType.CLIENT && (
            <BaseTextField
              disabled={true}
              label="Client Name"
              isDefaultField={false}
              labelShrink={false}
              optional={false}
              id="client"
              name="client"
              value={formik.values.client.clientName}
              // onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              /* error={
              formik.touched.companyName && Boolean(formik.errors.companyName)
            } */
              // helperText={formik.touched.companyName && formik.errors.companyName}
            />
          )}
          <BaseTextField
            label="Payment Terms"
            isDefaultField={false}
            labelShrink={false}
            optional={false}
            id="paymentTerms"
            name="paymentTerms"
            type="number"
            value={formik.values.paymentTerms}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.paymentTerms && Boolean(formik.errors.paymentTerms)
            }
            helperText={
              formik.touched.paymentTerms && formik.errors.paymentTerms
            }
            disabled
          />

          <BaseAutoSelect
            labelShrink={false}
            label="Company Entity "
            required={true}
            fullWidth
            options={formattedBranchesData}
            id="branchId"
            name="branchId"
            value={formik.values.branchId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.branchId && Boolean(formik.errors.branchId)}
            helperText={formik.touched.branchId && formik.errors.branchId}
            disabled
          />
          <BaseAutoSelect
            labelShrink={false}
            label="Bank Account"
            required={true}
            fullWidth
            options={formattedBankAccount}
            id="bankAccountId"
            name="bankAccountId"
            value={formik.values.bankAccountId}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={
              !formik.values.branchId || formattedBankAccount.length <= 1
            }
            error={
              formik.touched.bankAccountId &&
              Boolean(formik.errors.bankAccountId)
            }
            helperText={
              formik.touched.bankAccountId && formik.errors.bankAccountId
            }
          />

          <BaseAutoSelect
            labelShrink={false}
            disabled={true}
            label="Currency"
            required={true}
            fullWidth
            options={allCurrencies.map((currency) => ({
              label: currency,
              value: currency,
            }))}
            id="currency"
            name="currency"
            value={formik.values.currency}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              (formik.touched.currency && Boolean(formik.errors.currency)) ||
              (formik.values.selectedBankAccount &&
                formik.values.selectedBankAccount?.currency !== currency)
            }
            helperText={
              (formik.touched.currency && formik.errors.currency) ||
              (formik.values.selectedBankAccount &&
              formik.values.selectedBankAccount?.currency !== currency
                ? "Bank Account currency and filter currency doesn't matched"
                : "")
            }
          />

          <BaseAutoSelect
            labelShrink={false}
            label="Tax Type"
            required={true}
            fullWidth
            options={[
              {
                label: "GST",
                value: "GST",
              },
              {
                label: "VAT",
                value: "VAT",
              },
            ]}
            id="taxType"
            name="taxType"
            value={formik.values.taxType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.taxType && Boolean(formik.errors.taxType)}
            helperText={formik.touched.taxType && formik.errors.taxType}
            disabled
          />
          <BaseTextField
            label="Tax Percentage"
            isDefaultField={false}
            labelShrink={false}
            optional={false}
            id="taxPercentage"
            name="taxPercentage"
            type="number"
            value={formik.values.taxPercentage}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.taxPercentage &&
              Boolean(formik.errors.taxPercentage)
            }
            helperText={
              formik.touched.taxPercentage && formik.errors.taxPercentage
            }
            disabled
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6 mt-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className=" px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{
            marginRight: "16px",
          }}
          onClick={() => {
            close(false);
            /* setSelectedEmployment(null);
              formik.resetForm(); */
          }}
        >
          Cancel
        </BaseButton>

        <BaseButton
          isLoading={isCreateInvoiceApiLoading}
          disabled={
            !formik.isValid ||
            !formik.dirty ||
            formik.values.selectedBankAccount?.currency !== currency
          }
          loaderSize={24}
          loaderColor="primary"
          className="px-[20px] py-[5px]"
          color="primary"
          onClick={formik.handleSubmit}
        >
          Generate Invoice
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default GenerateInvoiceModal;
